// File: src/pages/LowStockAlert.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
  Badge,
} from "reactstrap";
import { ImDownload } from "react-icons/im";
import { FaFilter } from "react-icons/fa";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

/* ---- shared ---- */
import { CheckPermission } from "../import/CheckPermission";
import SuperAdminUI from "../import/SuperAdminUI";
import { exportDataToPDF } from "../import/Downloader";
import {
  _Get,
  PurchaseOrderList,
  createOrderhistoryviewDATABASE,
} from "../import/ApiEndPoint/ApiCalling";
import {
  WareHouse_Closing_Stock,
  Create_Warehouse_List,
  PurchaseProductList_Product,
} from "../import/ApiEndPoint/Api";

/* ---------- tiny page-scoped CSS (injected once) ---------- */
const injectOnce = () => {
  if (document.getElementById("lsa-inline-css")) return;
  const s = document.createElement("style");
  s.id = "lsa-inline-css";
  s.textContent = `
    .lsa-toolbar .count-pill{ background:#eef2ff; color:#3730a3; font-weight:800; padding:6px 10px; border-radius:999px; }
    .lsa-card{ border:1px solid #e5e7eb; border-radius:14px; background:#f4f7f6; padding:10px 12px; }
    .lsa-title{ font-weight:900; text-transform:uppercase; font-size:18px; letter-spacing:.02em; display:flex; gap:10px; align-items:center; flex-wrap:wrap; }
    .lsa-actions{ display:flex; gap:8px; align-items:center; flex-wrap:wrap; justify-content:flex-end; }
    .lsa-search{ min-width:260px; }
    .lsa-btn{
      height:34px; display:flex; align-items:center; justify-content:center; gap:8px;
      border-radius:12px; font-weight:800; letter-spacing:.02em;
    }
    .lsa-btn-outline{
      height:34px; display:flex; align-items:center; justify-content:center; gap:8px;
      border-radius:12px; font-weight:800; letter-spacing:.02em;
      background:#fff;
    }

    .lsa-filterbar{
      margin-top:10px;
      background:#fff;
      border:1px dashed #cbd5e1;
      border-radius:14px;
      padding:10px 12px;
    }
    .lsa-filter-row{
      display:flex;
      gap:10px;
      flex-wrap:wrap;
      align-items:flex-end;
    }
    .lsa-fitem{ flex:1 1 180px; min-width:180px; }
    .lsa-fitem label{ font-size:12px; font-weight:800; color:#0f172a; letter-spacing:.02em; margin-bottom:4px; }
    .lsa-fitem .form-control{ border-radius:12px; }
    .lsa-chipline{ margin-top:10px; display:flex; flex-wrap:wrap; gap:8px; }
    .lsa-chip{
      display:inline-flex; align-items:center; gap:8px;
      padding:6px 10px; border-radius:999px;
      background:#f1f5f9; border:1px solid #e5e7eb; color:#0f172a;
      font-weight:800; font-size:12px; letter-spacing:.02em;
    }
    .lsa-chip button{
      border:0; background:transparent; cursor:pointer; font-weight:900; color:#0b6bcb; padding:0; line-height:1;
    }

    .lsa-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .lsa-table{ width:100%; border-collapse:collapse; min-width:1100px; }
    .lsa-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:900; text-transform:uppercase; font-size:12px; letter-spacing:.04em;
      border-bottom:1px solid #e5e7eb; padding:8px 10px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .lsa-table tbody td{
      padding:8px 10px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }

    .group-row th, .group-row td{
      background:#f9fbff; font-weight:900; text-align:left !important; color:#0b6bcb;
      border-top:1px solid #e5e7eb; border-bottom:1px solid #e5e7eb;
      padding:10px 12px !important;
    }
    .group-actions{ display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
  `;
  document.head.appendChild(s);
};

/* ---------- helpers ---------- */
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const fmt2 = (n) => (n || n === 0 ? Number(n).toFixed(2) : "");
const normId = (x) => String(x?._id ?? x?.$oid ?? x?.id ?? x ?? "");
const safeGet = (obj, path, def = "") =>
  path
    .split(".")
    .reduce((a, k) => (a && a[k] !== undefined ? a[k] : undefined), obj) ?? def;

// parse numeric-like strings safely (handles "", null)
const toNum = (v, d = 0) => {
  const n = Number(String(v ?? "").replace(/[^\d.\-]/g, ""));
  return Number.isFinite(n) ? n : d;
};

// Numeric helper used by the Closing Stock Report calculation.
const num = (v) => (Number.isFinite(+v) ? +v : 0);

/* ================= Financial Year Helpers ================= */

const getFYStartYear = (date = new Date()) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = d.getMonth();
  return m >= 3 ? y : y - 1;
};

const getFinancialYearLabel = (date = new Date()) => {
  const start = getFYStartYear(date);
  return `${start}-${String(start + 1).slice(-2)}`;
};

const parseFYStart = (fy) => {
  const start = Number(String(fy || "").split("-")[0]);
  return Number.isFinite(start) ? start : getFYStartYear();
};

const getPreviousFY = (fy) => {
  const start = parseFYStart(fy);
  const prevStart = start - 1;
  return `${prevStart}-${String(prevStart + 1).slice(-2)}`;
};

const getSelectedFinancialYear = () =>
  localStorage.getItem("financialYearSelected") || getFinancialYearLabel();

const buildSelectedDatabase = (db, fy) =>
  `${db}-${fy || getSelectedFinancialYear()}`;

const safeJson = (v) => {
  if (!v) return null;
  if (typeof v === "object") return v;
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
};

const findFYEntry = (container, fy) => {
  const data = safeJson(container);
  if (!data) return null;

  if (Array.isArray(data)) {
    return (
      data.find(
        (x) =>
          String(
            x?.financialYear || x?.FinancialYear || x?.fy || x?.year || "",
          ) === String(fy),
      ) || null
    );
  }

  if (typeof data === "object") {
    return (
      data[fy] ||
      data[String(fy).replace("-", "_")] ||
      data[String(fy).replace("-", "/")] ||
      null
    );
  }

  return null;
};

const firstDefined = (...vals) => {
  for (const v of vals) {
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
};

const getOpeningFinancialYear = (row = {}) =>
  firstDefined(
    row?._productDetailsFromProductList?.openingFinancialYear,
    row?._productDetailsFromProductList?.OpeningFinancialYear,
    row?._productDetailsFromProductList?.financialYearOpeningYear,
    row?._productDetailsFromProductList?.openingFY,
    row?._productDetailsFromProductList?.OpeningFY,
    row?._productDetailsFromProductList?.openingYear,
    row?.openingFinancialYear,
    row?.OpeningFinancialYear,
    row?.financialYearOpeningYear,
    row?.productId?.openingFinancialYear,
    row?.productId?.OpeningFinancialYear,
    row?.product?.openingFinancialYear,
    row?.product?.OpeningFinancialYear,
  );

const getProductSavedOpening = (row = {}) => {
  const product =
    row?._productDetailsFromProductList ||
    (row?.productId && typeof row.productId === "object"
      ? row.productId
      : {}) ||
    row?.product ||
    {};

  const qty = firstDefined(
    product?.Opening_Stock,
    product?.openingStock,
    product?.openingQty,
    product?.oQty,
    product?.financialYearOpeningStock,
    product?.OpeningStock,
    row?.Opening_Stock,
    row?.openingStock,
    row?.openingQty,
    row?.oQty,
  );

  const rate = firstDefined(
    product?.openingRate,
    product?.Opening_Rate,
    product?.OpeningRate,
    product?.oRate,
    product?.purchaseRate,
    product?.PurchaseRate,
    product?.Product_Purchase_Rate,
    product?.Purchase_Price,
    product?.purchasePrice,
    product?.PurchasePrice,
    product?.costPrice,
    product?.Product_Rate,
    product?.price,
    row?.openingRate,
    row?.oRate,
    row?.price,
  );

  return {
    qty: num(qty),
    rate: num(rate),
    source: "Product saved opening",
  };
};

const extractBackendFYOpening = (row, fy) => {
  const directFY = String(
    row?.financialYear ||
      row?.openingFinancialYear ||
      row?.FinancialYear ||
      row?.fy ||
      "",
  );

  if (directFY && directFY === String(fy)) {
    const q = firstDefined(
      row?.financialYearOpeningStock,
      row?.openingStock,
      row?.openingQty,
      row?.Opening_Stock,
      row?.oQty,
    );

    const r = firstDefined(
      row?.financialYearOpeningRate,
      row?.openingRate,
      row?.oRate,
    );

    if (q !== undefined || r !== undefined) {
      return {
        qty: num(q),
        rate: num(r),
        source: "Backend FY opening",
      };
    }
  }

  const possible = [
    row?.openingStockByFY,
    row?.openingStocks,
    row?.financialYearOpenings,
    row?.openingByFinancialYear,
    row?.fyOpeningStock,
    row?.yearWiseOpeningStock,
    row?.productId?.openingStockByFY,
    row?.productId?.openingStocks,
    row?.productId?.financialYearOpenings,
    row?.productId?.openingByFinancialYear,
  ];

  for (const item of possible) {
    const entry = findFYEntry(item, fy);
    if (entry) {
      const q = firstDefined(
        entry?.openingStock,
        entry?.openingQty,
        entry?.Opening_Stock,
        entry?.oQty,
        entry?.qty,
        entry?.closingQty,
      );

      const r = firstDefined(
        entry?.openingRate,
        entry?.rate,
        entry?.closingAveRate,
        entry?.closingRate,
      );

      return {
        qty: num(q),
        rate: num(r),
        source: "Backend FY opening",
      };
    }
  }

  return null;
};

const stockSnapshotKey = (db, fy) => `FY_CLOSING_STOCK_SNAPSHOT:${db}:${fy}`;

const getProductIdForKey = (row) =>
  normId(
    row?._productIdResolved ||
      row?._productDetailsFromProductList?._id ||
      row?._productDetailsFromProductList?.id ||
      row?.productId?._id ||
      row?.productId ||
      row?.pId ||
      row?.ProductId ||
      row?.product?._id ||
      "",
  );

const getWarehouseIdForKey = (row) =>
  normId(
    row?._warehouseIdResolved ||
      row?.warehouseId ||
      row?.warehouse ||
      row?.warehouse?._id ||
      row?.warehouseId1 ||
      "",
  );

const getProductTitleForKey = (row = {}) =>
  String(
    row?.Product_Title ||
      row?._productDetailsFromProductList?.Product_Title ||
      row?._productDetailsFromProductList?.productName ||
      row?.productId?.Product_Title ||
      row?.productName ||
      row?.ProductName ||
      "",
  )
    .trim()
    .toLowerCase();

const makeStockKey = (row) => {
  const wid = getWarehouseIdForKey(row);
  const pid = getProductIdForKey(row);

  if (wid && pid) return `${wid}::${pid}`;

  const title = getProductTitleForKey(row);

  if (wid && title) return `${wid}::title:${title}`;

  if (pid) return `id:${pid}`;
  if (title) return `title:${title}`;

  return "";
};

const getStockLookupKeys = (row = {}) => {
  const keys = [];
  const wid = getWarehouseIdForKey(row);
  const pid = getProductIdForKey(row);
  const title = getProductTitleForKey(row);

  if (wid && pid) keys.push(`${wid}::${pid}`);
  if (wid && title) keys.push(`${wid}::title:${title}`);
  if (pid) keys.push(`id:${pid}`);
  if (title) keys.push(`title:${title}`);

  return [...new Set(keys.filter(Boolean))];
};

const addPreviousClosingToMap = (map, row, closing) => {
  const keys = getStockLookupKeys(row);
  if (!keys.length) return;

  const payload = {
    closingQty: num(closing?.closingQty),
    closingAveRate: num(closing?.closingAveRate),
    closingTotal: num(closing?.closingTotal),
  };

  keys.forEach((key) => {
    if (!map[key]) {
      map[key] = { ...payload };
      return;
    }

    // Product-only fallback keys can receive same product from multiple warehouses.
    // Sum qty/total and keep a weighted average rate so opening is still usable.
    const oldQty = num(map[key]?.closingQty);
    const oldTotal = num(map[key]?.closingTotal);
    const newQty = num(payload.closingQty);
    const newTotal = num(payload.closingTotal);
    const totalQty = oldQty + newQty;
    const totalValue = oldTotal + newTotal;

    map[key] = {
      closingQty: Number(totalQty.toFixed(2)),
      closingTotal: Number(totalValue.toFixed(2)),
      closingAveRate:
        totalQty > 0 ? Number((totalValue / totalQty).toFixed(2)) : 0,
    };
  });
};

const findPreviousClosingForRow = (row, previousClosingMap = {}) => {
  const keys = getStockLookupKeys(row);

  for (const key of keys) {
    if (previousClosingMap?.[key]) return previousClosingMap[key];
  }

  return null;
};

const getProductTitleKey = (item = {}) =>
  String(
    item?.Product_Title ||
      item?.productId?.Product_Title ||
      item?.productName ||
      item?.ProductName ||
      item?.name ||
      "",
  )
    .trim()
    .toLowerCase();

const getProductWarehouseId = (item = {}) =>
  normId(
    item?.warehouse?._id ||
      item?.warehouse ||
      item?.warehouseId ||
      item?.warehouseId1 ||
      item?.WarehouseId ||
      "",
  );

const buildProductDetailsMap = (products = []) => {
  const byId = new Map();
  const byTitle = new Map();
  const byWarehouseProductId = new Map();
  const byWarehouseTitle = new Map();

  (Array.isArray(products) ? products : []).forEach((product) => {
    const id = normId(product?._id || product?.id || product?.productId || "");
    const title = getProductTitleKey(product);
    const warehouseId = getProductWarehouseId(product);

    if (id) byId.set(id, product);
    if (title && !byTitle.has(title)) byTitle.set(title, product);
    if (warehouseId && id)
      byWarehouseProductId.set(`${warehouseId}::${id}`, product);
    if (warehouseId && title)
      byWarehouseTitle.set(`${warehouseId}::${title}`, product);
  });

  return {
    byId,
    byTitle,
    byWarehouseProductId,
    byWarehouseTitle,
    list: products,
  };
};

const findProductDetailsForRow = (row, productDetailsMap) => {
  if (!productDetailsMap) return null;

  const productId = getProductIdForKey(row);
  const warehouseId = getWarehouseIdForKey(row);
  const title = getProductTitleKey(row);

  if (warehouseId && productId) {
    const byWarehouseProductId = productDetailsMap.byWarehouseProductId?.get(
      `${warehouseId}::${productId}`,
    );
    if (byWarehouseProductId) return byWarehouseProductId;
  }

  if (productId) {
    const byId = productDetailsMap.byId?.get(String(productId));
    if (byId) return byId;
  }

  if (warehouseId && title) {
    const byWarehouseTitle = productDetailsMap.byWarehouseTitle?.get(
      `${warehouseId}::${title}`,
    );
    if (byWarehouseTitle) return byWarehouseTitle;
  }

  if (title) {
    const byTitle = productDetailsMap.byTitle?.get(title);
    if (byTitle) return byTitle;
  }

  return null;
};

const mergeProductDetailsIntoRow = (row, productDetailsMap) => {
  const product = findProductDetailsForRow(row, productDetailsMap);
  if (!product) return row;

  return {
    ...row,
    _productDetailsFromProductList: product,
    _productIdResolved: normId(product?._id || product?.id || row?.productId),
    productId: row?.productId || product?._id,
    Product_Title:
      row?.Product_Title ||
      row?.productId?.Product_Title ||
      product?.Product_Title ||
      product?.productName ||
      "",
    HSN_Code:
      row?.HSN_Code ||
      row?.productId?.HSN_Code ||
      product?.HSN_Code ||
      product?.hsn ||
      "",
    openingFinancialYear: firstDefined(
      product?.openingFinancialYear,
      product?.OpeningFinancialYear,
      product?.financialYearOpeningYear,
      product?.openingFY,
      product?.OpeningFY,
      product?.openingYear,
      row?.openingFinancialYear,
      row?.OpeningFinancialYear,
    ),
    Opening_Stock: firstDefined(
      product?.Opening_Stock,
      product?.openingStock,
      product?.openingQty,
      row?.Opening_Stock,
      row?.openingStock,
      row?.oQty,
    ),
    openingRate: firstDefined(
      product?.openingRate,
      product?.Opening_Rate,
      product?.OpeningRate,
      product?.oRate,
      product?.purchaseRate,
      product?.PurchaseRate,
      product?.Product_Purchase_Rate,
      product?.Purchase_Price,
      product?.purchasePrice,
      product?.PurchasePrice,
      product?.costPrice,
      product?.Product_Rate,
      row?.openingRate,
      row?.oRate,
    ),
  };
};

const includeMissingProductRows = (
  rawRows = [],
  productList = [],
  whMap = new Map(),
) => {
  const rows = Array.isArray(rawRows) ? [...rawRows] : [];
  const existingProductIds = new Set(
    rows.map((row) => getProductIdForKey(row)).filter(Boolean),
  );

  (Array.isArray(productList) ? productList : []).forEach((product) => {
    const productId = normId(
      product?._id || product?.id || product?.productId || "",
    );

    if (!productId || existingProductIds.has(productId)) return;

    const warehouseId = getProductWarehouseId(product);
    const warehouseName =
      product?.warehouse?.warehouseName ||
      product?.warehouseName ||
      whMap.get(String(warehouseId)) ||
      "";

    rows.push({
      _id: `product-master-${productId}`,
      productId,
      warehouseId,
      warehouseName,
      Product_Title: product?.Product_Title || product?.productName || "",
      HSN_Code: product?.HSN_Code || product?.hsn || "",
      date: product?.updatedAt || product?.createdAt || "",
      _fromProductMasterOnly: true,
    });

    existingProductIds.add(productId);
  });

  return rows;
};

const loadPreviousSnapshotOpening = (db, fy, row) => {
  try {
    const prevFY = getPreviousFY(fy);
    const raw = localStorage.getItem(stockSnapshotKey(db, prevFY));
    const snap = raw ? JSON.parse(raw) : {};
    const item = findPreviousClosingForRow(row, snap);

    if (!item) return null;

    return {
      qty: num(item?.closingQty),
      rate: num(item?.closingAveRate),
      source: `Previous FY closing snapshot (${prevFY})`,
    };
  } catch {
    return null;
  }
};

const getOpeningForFY = (row, fy, db, previousClosingMap = {}) => {
  const openingFY = getOpeningFinancialYear(row);
  const selectedStart = parseFYStart(fy);
  const openingStart = openingFY ? parseFYStart(openingFY) : null;
  const productOpening = getProductSavedOpening(row);

  // Product opening belongs to a future FY, so it must not appear in older reports.
  if (openingFY && openingStart > selectedStart) {
    return {
      qty: 0,
      rate: 0,
      source: `Product opening FY ${openingFY} is after selected FY`,
    };
  }

  // If product's openingFinancialYear is the selected FY,
  // opening must come directly from the product master opening information.
  if (openingFY && openingStart === selectedStart) {
    return {
      qty: num(productOpening?.qty),
      rate: num(productOpening?.rate),
      source: `Product opening (${openingFY})`,
    };
  }

  // If product opening belongs to an older FY,
  // selected FY opening must be previous FY closing.
  if (openingFY && openingStart < selectedStart) {
    const previousFromApi = findPreviousClosingForRow(row, previousClosingMap);

    if (previousFromApi) {
      return {
        qty: num(previousFromApi?.closingQty),
        rate: num(previousFromApi?.closingAveRate),
        source: `Previous FY closing (${getPreviousFY(fy)})`,
      };
    }

    const previous = loadPreviousSnapshotOpening(db, fy, row);
    if (previous) return previous;

    return {
      qty: 0,
      rate: 0,
      source: `Previous FY closing not found (${getPreviousFY(fy)})`,
    };
  }

  const backend = extractBackendFYOpening(row, fy);
  if (backend) return backend;

  if (productOpening?.qty || productOpening?.rate) {
    return {
      qty: num(productOpening?.qty),
      rate: num(productOpening?.rate),
      source: "Product saved opening",
    };
  }

  return {
    qty: num(row?.oQty ?? row?.openingQty ?? row?.Opening_Stock),
    rate: num(row?.openingRate ?? row?.oRate),
    source: "Legacy product opening",
  };
};

const normalizeRowsFromResponse = (res) => {
  if (Array.isArray(res?.data)) return res.data;

  if (!Array.isArray(res?.Warehouse)) return [];

  const out = [];

  res.Warehouse.forEach((wh) => {
    if (Array.isArray(wh?.productItems)) {
      wh.productItems.forEach((it, index) => {
        const productObj =
          it?.productId && typeof it.productId === "object" ? it.productId : {};

        const openingQty = firstDefined(
          it?.oQty,
          it?.openingQty,
          it?.Opening_Stock,
        );

        const openingRate = firstDefined(
          it?.openingRate,
          it?.oRate,
          it?.price,
          it?.cRate,
        );

        out.push({
          ...it,
          _id: it?._id || `${wh?._id || wh?.warehouseId}-${index}`,
          warehouseId: wh?.warehouseId || wh?._id || wh?.warehouseId1,
          warehouseName: wh?.warehouseName || "",
          Product_Title:
            it?.Product_Title ||
            productObj?.Product_Title ||
            it?.productName ||
            "",
          HSN_Code:
            it?.HSN_Code || productObj?.HSN_Code || productObj?.hsn || "",
          productId: it?.productId,
          date: wh?.date || it?.date || wh?.createdAt || it?.createdAt,

          oQty: openingQty,
          openingRate,
          openingCombineTotal:
            it?.openingCombineTotal ?? num(openingQty) * num(openingRate),

          pQty: it?.pQty ?? it?.purchaseQty ?? 0,
          avgPurchaseRate: it?.avgPurchaseRate ?? it?.pRate ?? 0,
          totalPurchaseData:
            it?.totalPurchaseData ?? it?.pTotal ?? it?.pBAmount ?? 0,

          pendingStock: it?.pendingStock ?? 0,
          pendingStockTotal: it?.pendingStockTotal ?? 0,

          sQty: it?.sQty ?? it?.saleQty ?? 0,
          sRate: it?.sRate ?? 0,
          sTotal: it?.sTotal ?? 0,

          closingQty: it?.closingQty ?? it?.cQty ?? it?.currentStock,
          closingAveRate: it?.closingAveRate ?? it?.cRate ?? it?.price,
          closingTotal: it?.closingTotal ?? it?.cTotal ?? it?.totalPrice,
        });
      });
    } else {
      out.push(wh);
    }
  });

  return out;
};

// A product is also shown when it is only slightly above the configured minimum.
const NEAR_MINIMUM_PERCENT = 10;
const NEAR_MINIMUM_MIN_UNITS = 1;

const getMinimumStock = (row = {}) => {
  const product =
    row?._productDetailsFromProductList ||
    (row?.productId && typeof row.productId === "object"
      ? row.productId
      : {}) ||
    row?.product ||
    {};

  return num(
    firstDefined(
      product?.MIN_stockalert,
      product?.minStockAlert,
      product?.minimumStockAlert,
      product?.minimumStock,
      product?.minStock,
      product?.Min_Stock_Alert,
      product?.Minimum_Stock_Alert,
      row?.MIN_stockalert,
      row?.minStockAlert,
      row?.minimumStockAlert,
      row?.minimumStock,
      row?.minStock,
    ),
  );
};

const getNearMinimumLimit = (minimumStock) => {
  const min = num(minimumStock);
  const buffer = Math.max(
    NEAR_MINIMUM_MIN_UNITS,
    (min * NEAR_MINIMUM_PERCENT) / 100,
  );
  return Number((min + buffer).toFixed(2));
};

const getLowStockLevel = (closingStock, minimumStock) => {
  const closing = num(closingStock);
  const minimum = num(minimumStock);

  if (closing <= 0) return "OUT OF STOCK";
  if (closing < minimum) return "LOW STOCK";
  if (closing === minimum) return "MINIMUM REACHED";
  return "NEAR MINIMUM";
};

// statuses that mean "this order/item should NOT count as placed"
const CANCELLED_STATES = new Set([
  "deactive",
  "cancel",
  "cancelled",
  "canceled",
  "return",
  "returned",
  "rejected",
]);

const orderedKey = (db) => `LowStockOrdered:${db}`;
const getOrderedSet = (db) => {
  try {
    return new Set(JSON.parse(localStorage.getItem(orderedKey(db)) || "[]"));
  } catch {
    return new Set();
  }
};
const setOrderedSet = (db, ids) => {
  const uniq = Array.from(new Set(ids));
  localStorage.setItem(orderedKey(db), JSON.stringify(uniq));
};

const norm = (s) => (s || "").toString().trim().toLowerCase();

// react element -> text for exports
const extractText = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean")
    return String(v);
  if (Array.isArray(v)) return v.map(extractText).join(" ").trim();
  if (React.isValidElement(v)) return extractText(v.props?.children);
  return "";
};

export default function LowStockAlert() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const [permissions, setPermissions] = useState({
    View: true,
    Create: true,
    Edit: true,
    Delete: true,
    Download: true,
  });
  const [masterShow, setMasterShow] = useState(false);

  const [rows, setRows] = useState([]); // ALL enriched rows (ordered + pending)
  const [search, setSearch] = useState("");

  // redesigned filters
  const [showFilters, setShowFilters] = useState(false);
  const [fWarehouse, setFWarehouse] = useState("All");
  const [fCategory, setFCategory] = useState("All");
  const [fSubCategory, setFSubCategory] = useState("All");
  const [fParty, setFParty] = useState("");
  const [showOrdered, setShowOrdered] = useState(false); // NEW: toggle show already ordered items

  // column chooser
  const [modal, setModal] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const addBufferRef = useRef([]);

  const [currentDb, setCurrentDb] = useState("");
  const [currentFY, setCurrentFY] = useState(getSelectedFinancialYear());
  const [scannedCount, setScannedCount] = useState(0);

  // Kept page-scoped because the imported Closing Stock calculation updates these.
  const [, setWarehouses] = useState([]);
  const [, setProductOutwardStockMap] = useState({});

  useEffect(() => injectOnce(), []);

  /* ---------- columns ---------- */
  const baseColumns = useMemo(
    () => [
      {
        key: "financialYear",
        header: "FINANCIAL YEAR",
        get: (r) => r?._financialYear || currentFY || "-",
      },
      {
        key: "warehouse",
        header: "WAREHOUSE",
        get: (r) => r?.warehouseName || r?._warehouseNameResolved || "-",
      },
      { key: "category", header: "CATEGORY", get: (r) => r?.category || "-" },
      {
        key: "SubCategory",
        header: "SUB CATEGORY",
        get: (r) => r?.SubCategory || "-",
      },
      {
        key: "title",
        header: "PRODUCT NAME",
        get: (r) => r?.Product_Title || "-",
      },
      {
        key: "min",
        header: "MIN STOCK",
        get: (r) => fmt2(r?.MIN_stockalert),
      },
      {
        key: "current",
        header: "CLOSING STOCK",
        get: (r) => fmt2(r?.closingStock),
      },
      {
        key: "gap",
        header: "CLOSING - MIN",
        get: (r) => fmt2(r?.stockGap),
      },
      {
        key: "alertLevel",
        header: "STOCK ALERT",
        get: (r) => r?.alertLevel || "-",
        render: (r) => {
          const level = r?.alertLevel || "";
          const color =
            level === "OUT OF STOCK"
              ? "danger"
              : level === "LOW STOCK"
                ? "danger"
                : level === "MINIMUM REACHED"
                  ? "warning"
                  : "info";
          return <Badge color={color}>{level || "-"}</Badge>;
        },
      },
      {
        key: "lastPrice",
        header: "LAST PURCHASE PRICE",
        get: (r) => r?.lastPurchasePrice,
        render: (r) =>
          r?.lastPurchasePrice !== undefined && r?.lastPurchasePrice !== ""
            ? fmt2(r.lastPurchasePrice)
            : "-",
      },
      {
        key: "lastParty",
        header: "LAST PARTY NAME",
        get: (r) => r?.lastPartyName || "-",
      },
      {
        key: "statusPlaced",
        header: "ORDER STATUS",
        get: (r) => (r?._isOrderedActive ? "ORDER PLACED" : "PENDING"),
        render: (r) =>
          r?._isOrderedActive ? (
            <Badge color="success">ORDER PLACED</Badge>
          ) : (
            <Badge color="warning">PENDING</Badge>
          ),
      },
      {
        key: "action",
        header: "CREATE ORDER",
        get: () => "",
        render: (r) =>
          r?._isOrderedActive ? (
            <span style={{ color: "#6b7280" }}>—</span>
          ) : (
            <Badge
              color="primary"
              style={{ cursor: "pointer" }}
              title="Create order for this party (includes all pending low-stock items from this party)"
              onClick={(e) => {
                e.preventDefault();
                handleCreateOrderForRow(r);
              }}
            >
              Create Order
            </Badge>
          ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, currentFY],
  );

  const colMap = useMemo(() => {
    const m = new Map();
    baseColumns.forEach((c) => m.set(c.key, c));
    return m;
  }, [baseColumns]);

  const [selectedKeys, setSelectedKeys] = useState([]);
  useEffect(() => {
    const raw = localStorage.getItem("LowStockAlertClosingCols");
    let next = [];
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length)
        next = parsed.filter((k) => colMap.has(k));
    } catch {}
    if (!next.length) next = baseColumns.map((c) => c.key);
    setSelectedKeys(next);
  }, [colMap, baseColumns]);

  const visibleCols = useMemo(
    () => selectedKeys.map((k) => colMap.get(k)).filter(Boolean),
    [selectedKeys, colMap],
  );

  /* ---------- helpers from order history ---------- */

  // Find the latest purchase price and supplier for every product.
  const buildLastInfoMap = (orderHistory = []) => {
    const map = new Map();

    orderHistory
      .slice()
      .sort((a, b) => {
        const da = new Date(a?.date || a?.createdAt || 0).getTime();
        const db = new Date(b?.date || b?.createdAt || 0).getTime();
        return db - da;
      })
      .forEach((ord) => {
        const partyName = String(
          ord?.fullName ||
            ord?.partyName ||
            ord?.partyId?.CompanyName ||
            ord?.customer?.CompanyName ||
            ord?.party?.CompanyName ||
            "",
        ).trim();

        const partyId = normId(
          ord?.partyId?._id ||
            ord?.partyId ||
            ord?.customer?._id ||
            ord?.customer ||
            ord?.party?._id ||
            ord?.party ||
            "",
        );

        const items = Array.isArray(ord?.orderItems) ? ord.orderItems : [];

        for (const it of items) {
          const pid = normId(
            it?.productId?._id ||
              it?.productId ||
              it?.product?._id ||
              it?.product ||
              "",
          );

          if (!pid || map.has(pid)) continue;

          const price = firstDefined(
            it?.price,
            it?.basicPrice,
            it?.rate,
            it?.purchaseRate,
            it?.PurchaseRate,
            "",
          );

          map.set(pid, { price, partyName, partyId });
        }
      });

    return map;
  };

  // Sum "outstanding ordered qty" per product, ignoring cancelled/returned/deleted items
  const buildOutstandingQtyByProduct = (orderHistory = []) => {
    const out = new Map(); // pid -> outstanding qty
    for (const ord of orderHistory || []) {
      const s = String(ord?.status || "")
        .trim()
        .toLowerCase();
      if (CANCELLED_STATES.has(s)) continue;
      if (
        s.includes("completed") ||
        s.includes("received") ||
        s.includes("closed")
      )
        continue;

      const items = Array.isArray(ord?.orderItems) ? ord.orderItems : [];
      for (const it of items) {
        const itemState = String(it?.status || "")
          .trim()
          .toLowerCase();
        if (CANCELLED_STATES.has(itemState)) continue;

        const pid = normId(it?.productId);
        if (!pid) continue;

        const orderedQty = toNum(it?.qty, 0);
        const receivedQty = toNum(it?.ReceiveQty, 0);
        const outstanding = Math.max(orderedQty - receivedQty, 0);

        if (outstanding > 0) out.set(pid, (out.get(pid) || 0) + outstanding);
      }
    }
    return out;
  };

  /* ---------- Closing Stock Report calculation ---------- */
  const getCurrentScope = () => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}");
    const role = user?.rolename?.roleName;

    let db = user?.database || "";
    let uid = user?._id || "";

    if (role === "MASTER") {
      const raw = localStorage.getItem("SuperadminIdByMaster");

      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          const text = String(parsed || "");
          const parts = text.split(" ");
          if (parts[0]) uid = parts[0];
          if (parts[1]) db = parts[1];
        } catch {
          const text = String(raw || "").replace(/^"|"$/g, "");
          const parts = text.split(" ");
          if (parts[0]) uid = parts[0];
          if (parts[1]) db = parts[1];
        }
      }
    }

    return { user, db, uid, role };
  };

  const loadWarehouses = async (db) => {
    const resp = await _Get(Create_Warehouse_List, db);
    const list = Array.isArray(resp?.Warehouse) ? resp.Warehouse : [];
    setWarehouses(list);

    const mp = new Map(
      list.map((w) => [String(w?._id), w?.warehouseName || ""]),
    );

    return { list, map: mp };
  };

  const loadProductDetailsMap = async (db) => {
    const res = await _Get(PurchaseProductList_Product, db);
    const productList = Array.isArray(res?.Product)
      ? res.Product
      : Array.isArray(res?.Productlist)
        ? res.Productlist
        : Array.isArray(res?.products)
          ? res.products
          : Array.isArray(res)
            ? res
            : [];

    return buildProductDetailsMap(productList);
  };

  const getInvoiceItemProductId = (it = {}) =>
    normId(
      it?.productId?._id ||
        it?.productId?.id ||
        it?.productId ||
        it?.ProductId ||
        it?.product_id ||
        it?.product?._id ||
        "",
    );

  const getInvoiceItemTitle = (it = {}) =>
    String(
      it?.productId?.Product_Title ||
        it?.productData?.Product_Title ||
        it?.product?.Product_Title ||
        it?.Product_Title ||
        it?.productName ||
        it?.ProductName ||
        "",
    )
      .trim()
      .toLowerCase();

  const pendingStockStatuses = [
    "pending",
    "billing",
    "dispatch",
    "delivery boy assigned",
    "out for delivery",
  ];

  const isPendingStockStatus = (status) => {
    const s = String(status || "")
      .trim()
      .toLowerCase();
    if (!s || s.includes("cancel") || s.includes("completed")) return false;
    return pendingStockStatuses.some((code) => s.includes(code));
  };

  const buildStockMapFromInvoices = (invoices = [], statusFilter = null) => {
    const bucket = {};

    const addToBucket = (key, qty, price) => {
      if (!key || qty <= 0) return;

      if (!bucket[key]) {
        bucket[key] = {
          qty: 0,
          amount: 0,
          priceSum: 0,
          priceCount: 0,
        };
      }

      bucket[key].qty += qty;
      bucket[key].amount += qty * price;
      bucket[key].priceSum += price;
      bucket[key].priceCount += 1;
    };

    invoices.forEach((invoice) => {
      const status = String(invoice?.status || "").toLowerCase();

      if (statusFilter && !statusFilter(status)) return;

      const items = Array.isArray(invoice?.orderItems)
        ? invoice.orderItems
        : [];

      items.forEach((it) => {
        const qty = num(
          it?.qty ?? it?.quantity ?? it?.Qty ?? it?.productQty ?? it?.saleQty,
        );

        const price = num(
          it?.price ??
            it?.rate ??
            it?.salesRate ??
            it?.saleRate ??
            it?.productId?.price ??
            it?.productId?.salesRate ??
            it?.productId?.saleRate,
        );

        const pid = getInvoiceItemProductId(it);
        const title = getInvoiceItemTitle(it);

        addToBucket(`id:${pid}`, qty, price);
        if (title) addToBucket(`title:${title}`, qty, price);
      });
    });

    const finalMap = {};

    Object.keys(bucket).forEach((key) => {
      const x = bucket[key];
      const avgRate =
        x.qty > 0
          ? x.amount / x.qty
          : x.priceCount > 0
            ? x.priceSum / x.priceCount
            : 0;

      finalMap[key] = {
        qty: Number(x.qty.toFixed(2)),
        rate: Number(avgRate.toFixed(2)),
        total: Number(x.amount.toFixed(2)),
      };
    });

    return finalMap;
  };

  const isCompletedPurchaseInvoiceStatus = (status) =>
    String(status || "")
      .trim()
      .toLowerCase()
      .includes("completed");

  const getPurchaseItemRate = (it = {}) =>
    num(
      it?.price ??
        it?.basicPrice ??
        it?.rate ??
        it?.purchaseRate ??
        it?.PurchaseRate ??
        it?.productData?.purchaseRate ??
        it?.productData?.PurchaseRate ??
        it?.productData?.price ??
        it?.productId?.purchaseRate ??
        it?.productId?.PurchaseRate ??
        it?.productId?.price,
    );

  const buildInwardStockMapFromPurchaseInvoices = (invoices = []) => {
    const bucket = {};

    const addToBucket = (key, qty, rate) => {
      if (!key || qty <= 0) return;

      if (!bucket[key]) {
        bucket[key] = {
          qty: 0,
          rateSum: 0,
          rateCount: 0,
        };
      }

      bucket[key].qty += qty;

      // Inward Rate = simple average of rates from all completed purchase invoices.
      if (rate > 0) {
        bucket[key].rateSum += rate;
        bucket[key].rateCount += 1;
      }
    };

    invoices.forEach((invoice) => {
      if (!isCompletedPurchaseInvoiceStatus(invoice?.status)) return;

      const items = Array.isArray(invoice?.orderItems)
        ? invoice.orderItems
        : [];

      items.forEach((it) => {
        const qty = num(
          it?.qty ?? it?.quantity ?? it?.Qty ?? it?.productQty ?? it?.pQty,
        );

        const rate = getPurchaseItemRate(it);
        const pid = getInvoiceItemProductId(it);
        const title = getInvoiceItemTitle(it);

        if (pid) addToBucket(`id:${pid}`, qty, rate);
        if (title) addToBucket(`title:${title}`, qty, rate);
      });
    });

    const finalMap = {};

    Object.keys(bucket).forEach((key) => {
      const x = bucket[key];
      const avgRate = x.rateCount > 0 ? x.rateSum / x.rateCount : 0;

      finalMap[key] = {
        qty: Number(x.qty.toFixed(2)),
        rate: Number(avgRate.toFixed(2)),
        total: Number((x.qty * avgRate).toFixed(2)),
      };
    });

    return finalMap;
  };

  const getPurchaseInvoiceListFromDatabase = async (db, fy) => {
    const selectedDatabase = buildSelectedDatabase(db, fy);
    const { uid } = getCurrentScope();
    const res = await PurchaseOrderList(uid, selectedDatabase);

    return Array.isArray(res?.orderHistory) ? res.orderHistory : [];
  };

  const loadInwardStockMap = async (db, fy) => {
    try {
      if (!db) return {};

      const invoices = await getPurchaseInvoiceListFromDatabase(db, fy);
      return buildInwardStockMapFromPurchaseInvoices(invoices);
    } catch (e) {
      console.error("[ClosingStockReport] inward stock load error:", e);
      return {};
    }
  };

  const findInwardStockForRow = (row, inwardMap) => {
    const pid = getProductIdForKey(row);

    if (pid) {
      const byId = inwardMap[`id:${String(pid)}`];
      if (byId) return byId;
    }

    const title = String(
      row?.Product_Title ||
        row?.productId?.Product_Title ||
        row?.productName ||
        row?.ProductName ||
        "",
    )
      .trim()
      .toLowerCase();

    if (title) {
      const byTitle = inwardMap[`title:${title}`];
      if (byTitle) return byTitle;
    }

    return { qty: 0, rate: 0, total: 0 };
  };

  const getSalesInvoiceListFromDatabase = async (db, fy) => {
    const selectedDatabase = buildSelectedDatabase(db, fy);
    const res = await createOrderhistoryviewDATABASE(selectedDatabase);

    return [
      ...(Array.isArray(res?.b2bData) ? res.b2bData : []),
      ...(Array.isArray(res?.b2csData) ? res.b2csData : []),
      ...(Array.isArray(res?.b2clData) ? res.b2clData : []),
      ...(Array.isArray(res?.orderHistory) ? res.orderHistory : []),
    ];
  };

  const loadPendingStockMap = async (db, fy) => {
    try {
      if (!db) return {};

      const invoices = await getSalesInvoiceListFromDatabase(db, fy);

      return buildStockMapFromInvoices(invoices, isPendingStockStatus);
    } catch (e) {
      console.error("[ClosingStockReport] pending stock load error:", e);
      return {};
    }
  };

  const loadOutwardStockMap = async (db, fy, shouldSetState = true) => {
    try {
      if (!db) {
        if (shouldSetState) setProductOutwardStockMap({});
        return {};
      }

      const invoices = await getSalesInvoiceListFromDatabase(db, fy);

      const finalMap = buildStockMapFromInvoices(
        invoices,
        (status) =>
          String(status || "")
            .trim()
            .toLowerCase() === "completed",
      );

      if (shouldSetState) setProductOutwardStockMap(finalMap);
      return finalMap;
    } catch (e) {
      console.error("[ClosingStockReport] outward stock load error:", e);
      if (shouldSetState) setProductOutwardStockMap({});
      return {};
    }
  };

  const findOutwardStockForRow = (row, outwardMap) => {
    const pid = getProductIdForKey(row);

    if (pid) {
      const byId = outwardMap[`id:${String(pid)}`];
      if (byId) return byId;
    }

    const title = String(
      row?.Product_Title ||
        row?.productId?.Product_Title ||
        row?.productName ||
        row?.ProductName ||
        "",
    )
      .trim()
      .toLowerCase();

    if (title) {
      const byTitle = outwardMap[`title:${title}`];
      if (byTitle) return byTitle;
    }

    return { qty: 0, rate: 0, total: 0 };
  };

  const findPendingStockForRow = (row, pendingMap) => {
    const pid = getProductIdForKey(row);

    if (pid) {
      const byId = pendingMap[`id:${String(pid)}`];
      if (byId) return byId;
    }

    const title = String(
      row?.Product_Title ||
        row?.productId?.Product_Title ||
        row?.productName ||
        row?.ProductName ||
        "",
    )
      .trim()
      .toLowerCase();

    if (title) {
      const byTitle = pendingMap[`title:${title}`];
      if (byTitle) return byTitle;
    }

    return { qty: 0, rate: 0, total: 0 };
  };

  const buildResolvedClosing = (row, opening, outward) => {
    const openingQty = num(opening?.qty);
    const openingRate = num(opening?.rate);
    const openingTotal = Number((openingQty * openingRate).toFixed(2));

    const inwardQty = num(row?.pQty);
    const inwardRate = num(row?.avgPurchaseRate);
    const inwardTotal = Number((inwardQty * inwardRate).toFixed(2));

    const pendingQty = num(row?.pendingStock);
    const outwardQty = num(outward?.qty);

    const closingQty = Number(
      (openingQty + inwardQty - pendingQty - outwardQty).toFixed(2),
    );

    // Closing Rate = (Opening Total + Inward Total) / (Opening Qty + Inward Qty)
    const totalAvailableQty = openingQty + inwardQty;
    const totalAvailableValue = openingTotal + inwardTotal;

    const closingAveRate =
      totalAvailableQty > 0 ? totalAvailableValue / totalAvailableQty : 0;

    // Closing Total = Closing Qty × Closing Rate
    const closingTotal = Number(
      (closingQty * closingAveRate.toFixed(2)).toFixed(2),
    );

    return {
      closingQty,
      closingAveRate: Number(closingAveRate.toFixed(2)),
      closingTotal,
    };
  };

  const resolveWarehouseForRow = (r, whMap) => {
    const idCandidate =
      r?.warehouseId ??
      r?.warehouse ??
      r?.warehouse?._id ??
      r?.warehouseId1 ??
      r?.productId?.warehouse ??
      r?.product?.warehouse ??
      r?.productId?.warehouseId ??
      r?.product?.warehouseId ??
      "";

    const wid = normId(idCandidate);
    const wname = r?.warehouseName || whMap.get(wid) || "";

    return {
      ...r,
      _warehouseIdResolved: wid,
      _warehouseNameResolved: wname,
    };
  };

  const loadPreviousClosingMap = async (
    db,
    fy,
    whMap,
    productDetailsMap,
    depth = 0,
  ) => {
    try {
      if (depth > 8) return {};

      const prevFY = getPreviousFY(fy);
      const dbParam = `${db}?financialYear=${encodeURIComponent(prevFY)}`;
      const res = await _Get(WareHouse_Closing_Stock, dbParam);
      const rawList = normalizeRowsFromResponse(res);
      const completeRawList = includeMissingProductRows(
        rawList,
        productDetailsMap?.list || [],
        whMap,
      );

      const mergedRawList = completeRawList.map((r) =>
        mergeProductDetailsIntoRow(
          resolveWarehouseForRow(r, whMap),
          productDetailsMap,
        ),
      );

      const prevStart = parseFYStart(prevFY);
      const needsOlderPreviousClosing = mergedRawList.some((row) => {
        const openingFY = getOpeningFinancialYear(row);
        if (!openingFY) return false;
        return parseFYStart(openingFY) < prevStart;
      });

      const olderPreviousClosingMap = needsOlderPreviousClosing
        ? await loadPreviousClosingMap(
            db,
            prevFY,
            whMap,
            productDetailsMap,
            depth + 1,
          )
        : {};

      const outwardMap = await loadOutwardStockMap(db, prevFY, false);
      const pendingMap = await loadPendingStockMap(db, prevFY);
      const inwardMap = await loadInwardStockMap(db, prevFY);

      const map = {};

      mergedRawList.forEach((rowWithProduct) => {
        const key = makeStockKey(rowWithProduct);
        if (!key) return;

        const opening = getOpeningForFY(
          rowWithProduct,
          prevFY,
          db,
          olderPreviousClosingMap,
        );

        const outward = findOutwardStockForRow(rowWithProduct, outwardMap);
        const pending = findPendingStockForRow(rowWithProduct, pendingMap);
        const inward = findInwardStockForRow(rowWithProduct, inwardMap);
        const rowWithPending = {
          ...rowWithProduct,
          pQty: num(inward?.qty),
          avgPurchaseRate: num(inward?.rate),
          totalPurchaseData: num(inward?.total),
          pendingStock: num(pending?.qty),
          pendingStockTotal: num(pending?.total),
        };
        const closing = buildResolvedClosing(rowWithPending, opening, outward);

        addPreviousClosingToMap(map, rowWithProduct, closing);
      });

      return map;
    } catch (e) {
      console.warn("[ClosingStockReport] previous FY closing load error:", e);
      return {};
    }
  };

  const rebuildRows = async (db, fy) => {
    const dbParam = fy ? `${db}?financialYear=${encodeURIComponent(fy)}` : db;

    const res = await _Get(WareHouse_Closing_Stock, dbParam);

    const rawList = normalizeRowsFromResponse(res);

    const { map: whMap } = await loadWarehouses(db);
    const productDetailsMap = await loadProductDetailsMap(db);
    const completeRawList = includeMissingProductRows(
      rawList,
      productDetailsMap?.list || [],
      whMap,
    );
    const previousClosingMap = await loadPreviousClosingMap(
      db,
      fy,
      whMap,
      productDetailsMap,
    );
    const outwardMap = await loadOutwardStockMap(db, fy);
    const pendingMap = await loadPendingStockMap(db, fy);
    const inwardMap = await loadInwardStockMap(db, fy);

    const withNames = completeRawList.map((r) => {
      const rowWithWH = resolveWarehouseForRow(r, whMap);
      const rowWithProduct = mergeProductDetailsIntoRow(
        rowWithWH,
        productDetailsMap,
      );
      const opening = getOpeningForFY(
        rowWithProduct,
        fy,
        db,
        previousClosingMap,
      );
      const outward = findOutwardStockForRow(rowWithProduct, outwardMap);
      const pending = findPendingStockForRow(rowWithProduct, pendingMap);
      const inward = findInwardStockForRow(rowWithProduct, inwardMap);
      const rowWithPending = {
        ...rowWithProduct,
        pQty: num(inward?.qty),
        avgPurchaseRate: num(inward?.rate),
        totalPurchaseData: num(inward?.total),
        pendingStock: num(pending?.qty),
        pendingStockTotal: num(pending?.total),
      };
      const closing = buildResolvedClosing(rowWithPending, opening, outward);

      const openingQty = num(opening?.qty);
      const openingRate = num(opening?.rate);

      return {
        ...rowWithPending,
        sQty: num(outward?.qty),
        closingQty: closing.closingQty,
        closingAveRate: closing.closingAveRate,
        closingTotal: closing.closingTotal,
        _openingQtyResolved: openingQty,
        _openingRateResolved: openingRate,
        _openingCombineTotalResolved: Number(
          (openingQty * openingRate).toFixed(2),
        ),
        _openingSourceResolved: opening?.source || "",
        _pendingRateResolved: num(pending?.rate),
        _avgSaleRateResolved: num(outward?.rate),
        _saleTotalResolved: num(outward?.total),
      };
    });

    const cleanRows = withNames.filter(
      (r) =>
        !r?._isSummary &&
        String(r?.warehouseName || "").toLowerCase() !== "total summary" &&
        String(
          r?.Product_Title || r?.productId?.Product_Title || "",
        ).toLowerCase() !== "total",
    );

    return cleanRows;
  };

  /* ---------- load closing stock + filter low/near-minimum products ---------- */
  const reload = async (uid, db) => {
    setLoading(true);
    setErrorMsg("");

    try {
      if (!uid || !db) {
        throw new Error("User or database information is missing.");
      }

      const fy = getSelectedFinancialYear();
      const selectedDatabase = buildSelectedDatabase(db, fy);

      setCurrentDb(selectedDatabase);
      setCurrentFY(fy);

      const inside = CheckPermission("Low Stock");
      if (inside && typeof inside === "object") {
        setPermissions((p) => ({ ...p, ...inside }));
      }

      // This is the same resolved data used by ClosingStockReport.jsx.
      const closingRows = await rebuildRows(db, fy);
      setScannedCount(closingRows.length);

      // Purchase history is used only for supplier/price and order-placed status.
      const purchaseRes = await PurchaseOrderList(uid, selectedDatabase).catch(
        () => ({}),
      );
      const orderHistory = Array.isArray(purchaseRes?.orderHistory)
        ? purchaseRes.orderHistory
        : [];

      const lastMap = buildLastInfoMap(orderHistory);
      const outstandingByPid = buildOutstandingQtyByProduct(orderHistory);

      const fromHistory = new Set(
        [...outstandingByPid.entries()]
          .filter(([_, qty]) => toNum(qty, 0) > 0)
          .map(([pid]) => pid),
      );

      const fromLocal = getOrderedSet(selectedDatabase);
      const mergedActive = new Set([...fromLocal, ...fromHistory]);

      const enriched = closingRows
        .map((row) => {
          const product =
            row?._productDetailsFromProductList ||
            (row?.productId && typeof row.productId === "object"
              ? row.productId
              : {}) ||
            {};

          const pid = getProductIdForKey(row);
          const minimumStock = getMinimumStock(row);
          const closingStock = num(row?.closingQty ?? row?.currentStock);
          const nearMinimumLimit = getNearMinimumLimit(minimumStock);

          // Ignore products without a positive minimum-stock alert.
          if (minimumStock <= 0) return null;

          // Keep lower, equal, or very close stock only.
          if (closingStock > nearMinimumLimit) return null;

          const li = lastMap.get(pid) || {};
          const lastPartyName = String(li?.partyName || "").trim();

          return {
            ...row,
            _financialYear: fy,
            productId: pid,
            warehouseName:
              row?._warehouseNameResolved || row?.warehouseName || "-",
            category: product?.category || row?.category || "-",
            SubCategory:
              product?.SubCategory ||
              product?.subCategory ||
              row?.SubCategory ||
              row?.subCategory ||
              "-",
            Product_Title:
              row?.Product_Title ||
              product?.Product_Title ||
              row?.productId?.Product_Title ||
              "",
            MIN_stockalert: minimumStock,
            currentStock: closingStock,
            closingStock,
            nearMinimumLimit,
            stockGap: Number((closingStock - minimumStock).toFixed(2)),
            alertLevel: getLowStockLevel(closingStock, minimumStock),
            lastPurchasePrice: li?.price ?? "",
            lastPartyName,
            _isOrderedActive: mergedActive.has(pid),
            _prefill: {
              productId: pid,
              HSN_Code: product?.HSN_Code || row?.HSN_Code || "",
              primaryUnit: product?.primaryUnit || "",
              secondaryUnit: product?.secondaryUnit || "",
              secondarySize: product?.secondarySize || 1,
              gstPercentage: product?.GSTRate || 0,
              Product_Title: product?.Product_Title || row?.Product_Title || "",
              lastPrice: li?.price ?? "",
            },
            _partyHint: {
              partyId: li?.partyId || "",
              partyName: lastPartyName,
            },
          };
        })
        .filter(Boolean)
        .sort((a, b) => {
          const severity = {
            "OUT OF STOCK": 0,
            "LOW STOCK": 1,
            "MINIMUM REACHED": 2,
            "NEAR MINIMUM": 3,
          };

          const bySeverity =
            (severity[a?.alertLevel] ?? 99) - (severity[b?.alertLevel] ?? 99);
          if (bySeverity !== 0) return bySeverity;

          const aRatio =
            num(a?.MIN_stockalert) > 0
              ? num(a?.closingStock) / num(a?.MIN_stockalert)
              : Infinity;
          const bRatio =
            num(b?.MIN_stockalert) > 0
              ? num(b?.closingStock) / num(b?.MIN_stockalert)
              : Infinity;

          return aRatio - bRatio;
        });

      setRows(enriched);

      // Server history remains the source of truth after refresh.
      try {
        setOrderedSet(selectedDatabase, Array.from(fromHistory));
      } catch {}
    } catch (e) {
      console.error("[LowStockAlert] closing-stock fetch error:", e);
      setErrorMsg(
        e?.response?.data?.message ||
          e?.message ||
          "Unable to load the closing-stock low-stock list.",
      );
      setRows([]);
      setScannedCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const { uid, db, role } = getCurrentScope();
      setMasterShow(role === "MASTER");
      await reload(uid, db);
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh on focus/tab visible (to reflect deletes/returns quickly)
  useEffect(() => {
    const refetch = () => {
      const { uid, db } = getCurrentScope();
      if (uid && db) reload(uid, db);
    };
    const onFocus = () => refetch();
    const onVis = () => {
      if (document.visibilityState === "visible") refetch();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  /* ---------- grouping by party ---------- */
  const makeGroups = (arr) => {
    const groups = new Map();
    for (const r of arr) {
      const key =
        (r?.lastPartyName && r.lastPartyName.trim()) ||
        (safeGet(r, "partyId.CompanyName", "") || "No Party").trim();
      if (!groups.has(key))
        groups.set(key, {
          partyName: key,
          partyId: r?._partyHint?.partyId || "",
          rows: [],
        });
      groups.get(key).rows.push(r);
    }
    return Array.from(groups.values());
  };

  const handleCreateOrderForGroup = (group) => {
    const items = group.rows
      .filter((r) => !r?._isOrderedActive)
      .map((r) => r._prefill);
    if (!items.length) return;
    navigate("/app/purchase-orders/create", {
      state: {
        isFromLowStock: "yes",
        partyName: group.partyName,
        partyId: group.partyId || "",
        items,
        db: currentDb,
      },
    });
  };

  const handleCreateOrderForRow = (row) => {
    const key =
      (row?.lastPartyName && row.lastPartyName.trim()) ||
      (safeGet(row, "partyId.CompanyName", "") || "No Party").trim();
    const groups = makeGroups(finalRows);
    const group = groups.find((g) => g.partyName === key);
    if (!group) return;
    handleCreateOrderForGroup(group);
  };

  /* ---------- column chooser ---------- */
  const openModal = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setModal(true);
  };
  const closeModal = () => setModal(false);
  const handleCheckbox = (checked, key) => {
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(key)) buf.push(key);
    } else {
      const i = buf.indexOf(key);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };
  const addSelectedColumns = () =>
    setSelectedKeys(
      Array.from(new Set([...selectedKeys, ...addBufferRef.current])),
    );
  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };
  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };
  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };
  const applyColumns = (e) => {
    e.preventDefault();
    localStorage.setItem(
      "LowStockAlertClosingCols",
      JSON.stringify(selectedKeys),
    );
    closeModal();
  };

  /* ---------- redesigned filtering pipeline ---------- */
  const baseDisplayRows = useMemo(() => {
    if (showOrdered) return rows;
    return rows.filter((r) => !r?._isOrderedActive);
  }, [rows, showOrdered]);

  const warehouseOptions = useMemo(() => {
    const set = new Set(
      (rows || [])
        .map((r) =>
          (r?.warehouseName || r?._warehouseNameResolved || "").trim(),
        )
        .filter(Boolean),
    );
    return ["All", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [rows]);

  const categoryOptions = useMemo(() => {
    const set = new Set(
      (rows || []).map((r) => (r?.category || "").trim()).filter(Boolean),
    );
    return ["All", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [rows]);

  const subCategoryOptions = useMemo(() => {
    const set = new Set(
      (rows || []).map((r) => (r?.SubCategory || "").trim()).filter(Boolean),
    );
    return ["All", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [rows]);

  const filteredByFilters = useMemo(() => {
    const partyTerm = norm(fParty);
    return (baseDisplayRows || []).filter((r) => {
      if (
        fWarehouse !== "All" &&
        (r?.warehouseName || r?._warehouseNameResolved || "-") !== fWarehouse
      )
        return false;
      if (fCategory !== "All" && (r?.category || "-") !== fCategory)
        return false;
      if (fSubCategory !== "All" && (r?.SubCategory || "-") !== fSubCategory)
        return false;

      if (partyTerm) {
        const p =
          (r?.lastPartyName && r.lastPartyName.trim()) ||
          (safeGet(r, "partyId.CompanyName", "") || "No Party").trim();
        if (!norm(p).includes(partyTerm)) return false;
      }
      return true;
    });
  }, [baseDisplayRows, fWarehouse, fCategory, fSubCategory, fParty]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return filteredByFilters;

    const searchCols = visibleCols;
    return (filteredByFilters || []).filter((r, idx) =>
      searchCols.some((c) => toStr(c.get(r, idx)).toLowerCase().includes(term)),
    );
  }, [filteredByFilters, search, visibleCols]);

  // used in row click grouping handler
  const finalRows = filteredRows;

  const clearAllFilters = () => {
    setFWarehouse("All");
    setFCategory("All");
    setFSubCategory("All");
    setFParty("");
    setShowOrdered(false);
  };

  const activeChips = useMemo(() => {
    const chips = [];
    if (fWarehouse !== "All") chips.push({ k: "Warehouse", v: fWarehouse });
    if (fCategory !== "All") chips.push({ k: "Category", v: fCategory });
    if (fSubCategory !== "All")
      chips.push({ k: "SubCategory", v: fSubCategory });
    if (fParty.trim()) chips.push({ k: "Party", v: fParty.trim() });
    if (showOrdered) chips.push({ k: "Show", v: "Ordered + Pending" });
    return chips;
  }, [fWarehouse, fCategory, fSubCategory, fParty, showOrdered]);

  /* ---------- exports (buttons) ---------- */
  const exportableCols = useMemo(
    () => visibleCols.filter((c) => c.key !== "action"),
    [visibleCols],
  );

  const cellText = (c, r, idx) => {
    const v = typeof c.render === "function" ? c.render(r, idx) : c.get(r, idx);
    return toStr(extractText(v) || v);
  };

  const handleExportPDF = async () => {
    const cols = exportableCols;
    const header = cols.map((c) => c.header).join(",");
    const lines = (filteredRows || []).map((r, idx) =>
      cols
        .map((c) => String(cellText(c, r, idx)).replace(/"/g, '""'))
        .join(","),
    );
    const csv = [header, ...lines].join("\r\n");
    await exportDataToPDF(csv, `LowStockClosing-${currentFY}`);
  };

  const handleExportXLSX = async () => {
    const cols = exportableCols;
    const data = (filteredRows || []).map((r, idx) => {
      const obj = {};
      cols.forEach((c) => {
        obj[c.header] = toStr(cellText(c, r, idx));
      });
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `LowStockClosing-${currentFY}.xlsx`,
    );
  };

  /* ---------- MASTER (SuperAdminUI) ---------- */
  const handleDropdownChange = (val) => {
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(val));
  };
  const handleMasterSubmit = async () => {
    const saved = JSON.parse(
      localStorage.getItem("SuperadminIdByMaster") || "null",
    );
    if (!saved) return;
    const [id, db] = String(saved).split(" ");
    await reload(id, db);
  };

  /* ---------- loading ---------- */
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filteredRows?.length;
  const groups = makeGroups(filteredRows);

  return (
    <>
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      {/* ===== Toolbar Card ===== */}
      <Row className="lsa-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card className="lsa-card">
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div className="lsa-title">
                Low Stock Report{" "}
                <span className="count-pill">{filteredRows.length}</span>
                <span
                  style={{ fontWeight: 800, color: "#64748b", fontSize: 12 }}
                >
                  (Scanned: {scannedCount} | FY: {currentFY})
                </span>
              </div>

              <div style={{ flex: "1 1 240px" }} />

              {masterShow ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <SuperAdminUI
                    onDropdownChange={handleDropdownChange}
                    onSubmit={handleMasterSubmit}
                  />
                </div>
              ) : null}

              <div className="lsa-actions">
                <div className="lsa-search">
                  <Input
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ background: "#fff", borderRadius: 12 }}
                  />
                </div>

                <Button
                  className="lsa-btn-outline"
                  outline
                  color="primary"
                  onClick={() => setShowFilters((s) => !s)}
                  title="Show/Hide Filters"
                >
                  <FaFilter size={14} /> Filters
                </Button>

                {permissions?.View && (
                  <Button
                    className="lsa-btn-outline"
                    outline
                    color="primary"
                    onClick={openModal}
                    title="Choose Columns"
                  >
                    <FaFilter size={14} /> Columns
                  </Button>
                )}

                {permissions?.Download && (
                  <>
                    <Button
                      className="lsa-btn"
                      color="primary"
                      onClick={handleExportXLSX}
                      title="Download Excel"
                    >
                      <ImDownload /> Excel
                    </Button>
                    {/* <Button
                      className="lsa-btn-outline"
                      outline
                      color="primary"
                      onClick={handleExportPDF}
                      title="Download PDF"
                    >
                      <ImDownload /> PDF
                    </Button> */}
                  </>
                )}
              </div>
            </div>

            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                fontWeight: 700,
                color: "#475569",
              }}
            >
              Source: Closing Stock Report calculation for {currentFY}. Products
              are shown when closing stock is at/below minimum or within{" "}
              {NEAR_MINIMUM_PERCENT}% (minimum 1 unit) above it.
            </div>

            {/* ===== Redesigned Filters Panel ===== */}
            {showFilters && (
              <div className="lsa-filterbar">
                <div className="lsa-filter-row">
                  <div className="lsa-fitem">
                    <label>Warehouse</label>
                    <Input
                      type="select"
                      value={fWarehouse}
                      onChange={(e) => setFWarehouse(e.target.value)}
                    >
                      {warehouseOptions.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </Input>
                  </div>

                  <div className="lsa-fitem">
                    <label>Category</label>
                    <Input
                      type="select"
                      value={fCategory}
                      onChange={(e) => setFCategory(e.target.value)}
                    >
                      {categoryOptions.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </Input>
                  </div>

                  <div className="lsa-fitem">
                    <label>Sub Category</label>
                    <Input
                      type="select"
                      value={fSubCategory}
                      onChange={(e) => setFSubCategory(e.target.value)}
                    >
                      {subCategoryOptions.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </Input>
                  </div>

                  <div className="lsa-fitem">
                    <label>Party (contains)</label>
                    <Input
                      placeholder="Type party name..."
                      value={fParty}
                      onChange={(e) => setFParty(e.target.value)}
                    />
                  </div>

                  <div className="lsa-fitem" style={{ minWidth: 220 }}>
                    <label>Show Ordered Items</label>
                    <div
                      style={{ display: "flex", gap: 8, alignItems: "center" }}
                    >
                      <Input
                        type="select"
                        value={showOrdered ? "YES" : "NO"}
                        onChange={(e) =>
                          setShowOrdered(e.target.value === "YES")
                        }
                      >
                        <option value="NO">NO (Default)</option>
                        <option value="YES">YES</option>
                      </Input>
                      <Button
                        className="lsa-btn-outline"
                        outline
                        color="secondary"
                        onClick={clearAllFilters}
                        title="Clear Filters"
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                </div>

                {!!activeChips.length && (
                  <div className="lsa-chipline">
                    {activeChips.map((c, i) => (
                      <span className="lsa-chip" key={`${c.k}-${i}`}>
                        {c.k}: {c.v}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* ===== Grouped List ===== */}
      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No records found.
              </div>
            ) : (
              <div className="lsa-table-wrap">
                <table className="lsa-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((g, gi) => {
                      const pending = g.rows.filter(
                        (r) => !r?._isOrderedActive,
                      );
                      const allOrdered = g.rows.every(
                        (r) => !!r?._isOrderedActive,
                      );
                      return (
                        <React.Fragment key={`g-${gi}`}>
                          <tr className="group-row">
                            <td colSpan={visibleCols.length}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: 10,
                                  flexWrap: "wrap",
                                }}
                              >
                                <div>
                                  PARTY:&nbsp;
                                  <span style={{ color: "#0f172a" }}>
                                    {g.partyName || "-"}
                                  </span>
                                  {allOrdered && (
                                    <span style={{ marginLeft: 10 }}>
                                      <Badge color="success">All Ordered</Badge>
                                    </span>
                                  )}
                                </div>

                                <div className="group-actions">
                                  {!pending.length ? null : (
                                    <Button
                                      color="primary"
                                      size="sm"
                                      title="Create order for all pending products of this party"
                                      onClick={() =>
                                        handleCreateOrderForGroup(g)
                                      }
                                    >
                                      Create Order ({pending.length})
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>

                          {g.rows.map((r, idx) => (
                            <tr key={r?._id || r?.productId || `${gi}-${idx}`}>
                              {visibleCols.map((c) => (
                                <td key={c.key}>
                                  {typeof c.render === "function"
                                    ? c.render(r, idx)
                                    : toStr(c.get(r, idx))}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* ===== Column Chooser ===== */}
      <Modal
        isOpen={modal}
        toggle={closeModal}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={closeModal}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h5>Available Columns</h5>
              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  maxHeight: 280,
                  overflow: "auto",
                  background: "#fff",
                  padding: 8,
                }}
              >
                {baseColumns.map((col) => (
                  <div
                    key={col.key}
                    style={{
                      padding: "8px 10px",
                      borderRadius: 8,
                      border: "1px solid #e5e7eb",
                      marginBottom: 6,
                      background: "#fff",
                    }}
                  >
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleCheckbox(e.target.checked, col.key)
                        }
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12" xl="8" xs="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    maxHeight: 280,
                    overflow: "auto",
                    background: "#fff",
                    padding: 8,
                    flex: 1,
                  }}
                >
                  {selectedKeys.map((key, i) => {
                    const col = colMap.get(key);
                    if (!col) return null;
                    return (
                      <div
                        key={key}
                        style={{
                          padding: "8px 10px",
                          borderRadius: 8,
                          border: "1px solid #e5e7eb",
                          marginBottom: 6,
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </>
  );
}
